/**
 * Forgot Password Page
 * Request password reset link
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, KeyRound, CheckCircle2 } from 'lucide-react';
import { authApi } from '../../lib/api';
import vizzexLogo from '../../assets/VIZZEX.250.png';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authApi.requestPasswordReset(email);
      
      if (response.success) {
        setSent(true);
      } else {
        // Unexpected — backend should always return success:true on 200
        setError('Something went wrong. Please try again.');
      }
    } catch (err: any) {
      console.error('Password reset request error:', err);
      const status = err?.response?.status;
      if (status && status < 500) {
        // 4xx treated the same as success to avoid email enumeration
        setSent(true);
      } else {
        // Genuine infrastructure failure — let the user know
        setError('Something went wrong. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={vizzexLogo} 
            alt="VizzEx Pro" 
            className="w-[250px] mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          <p className="text-[var(--text-secondary)] mt-2">Reset your password</p>
        </div>

        <div className="glass rounded-2xl p-8">
          {sent ? (
            // Success state
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[var(--color-success)]" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Check your email</h2>
              <p className="text-[var(--text-secondary)] mb-6">
                If an account exists for <strong>{email}</strong>, 
                you'll receive a password reset link shortly.
              </p>
              <p className="text-sm text-white/70 mb-4">
                The link will expire in 1 hour.
              </p>
              <Link
                to="/login"
                className="inline-block px-6 py-3 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            // Request form
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center mx-auto mb-3">
                  <KeyRound className="w-6 h-6 text-[var(--color-primary-400)]" />
                </div>
                <p className="text-[var(--text-secondary)] text-sm">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)] text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[var(--color-primary-400)] focus:ring-1 focus:ring-[var(--color-primary-400)] transition-colors"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)] text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          )}

          {/* Back to login */}
          {!sent && (
            <div className="mt-6 pt-6 border-t border-[var(--bg-tertiary)] text-center">
              <Link to="/login" className="text-sm text-[var(--text-secondary)] hover:text-[var(--color-primary-400)]">
                ← Back to login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


